import NextLink from 'next/link';
import { Flex, Link, SimpleGrid, Image, Text } from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { sendAnalyticsEvent } from '../common/libs/googleAnalytics';
import { getPublicRuntimeConfig } from '../common/config';
import { SPECIAL_PAGE_GA_CATEGORY } from '../common/constants';
import { generateS3DownloadUrl } from '../server/libs/siteSettings';

const { slideWebBaseUrl } = getPublicRuntimeConfig();
const isExternalUrl = (url: string | null) => {
  return !!url && url.startsWith('http') && !url.startsWith(slideWebBaseUrl);
};

export type SpecialPageLinkItem = {
  title: string;
  image?: string; // 画像パス（imageS3Keyよりも優先される）
  imageS3Key?: string; // 画像S3キー
  link: string;
  label?: string | null;
  labelSuffix?: string;
};

/**
 * 特集ページテンプレートでで使うリンク要素
 */
const SpecialPageLinks = ({
  links,
  isMobile,
  maxHeight,
  grid,
  gaEventLabelPrefix,
  labelJustifyContent,
}: {
  links: SpecialPageLinkItem[];
  isMobile: boolean;
  maxHeight: string;
  grid?: [number, number, number] | undefined;
  gaEventLabelPrefix: string;
  labelJustifyContent?: 'flex-start' | 'center' | 'flex-end';
}) => {
  if (!links.length) return null;
  const list = links.map(
    ({ title, link, image, imageS3Key, label, labelSuffix }, index) => (
      <NextLink key={link} href={link} passHref>
        <Link
          isExternal={isExternalUrl(link)}
          onClick={() =>
            sendAnalyticsEvent(
              SPECIAL_PAGE_GA_CATEGORY,
              'click',
              `${gaEventLabelPrefix}_${title}`,
            )
          }
        >
          <Image
            src={image ?? generateS3DownloadUrl(imageS3Key)}
            alt={title}
            w="auto"
            maxH={isMobile ? undefined : maxHeight}
            borderRadius={4}
            border="1px solid"
            borderColor="borderLight"
          />
          {label && (
            <Flex
              fontSize="xs"
              color="textSecondary"
              mt={1}
              justifyContent={labelJustifyContent ?? 'flex-start'}
              alignItems="center"
              flexWrap="wrap"
            >
              <Text textDecoration="underline">
                {label} <ExternalLinkIcon />
              </Text>
              {labelSuffix && (
                <Text textDecoration="underline">{labelSuffix}</Text>
              )}
            </Flex>
          )}
        </Link>
      </NextLink>
    ),
  );

  if (!!grid) {
    return (
      <SimpleGrid columns={grid} spacing={4} w="full">
        {list}
      </SimpleGrid>
    );
  }

  return (
    <Flex justifyContent="center" gap={4} flexWrap="wrap" py={2}>
      {list}
    </Flex>
  );
};

export default SpecialPageLinks;
